/**
 * Pure helpers for detecting duplicate players within a single team.
 *
 * Kept separate from the dialog component so callers can use the
 * detection logic without loading the React + MUI dialog code. This
 * unblocks lazy-loading the dialog itself in places that only need the
 * detection (e.g. the on-fixture-load scan in TXLive).
 *
 *  - findSquadDuplicates(players, teamLabel) — scan one team for any
 *    collisions; one entry per (field, value) with a members[] list.
 *  - findPlayerDuplicates(candidate, teammates) — pre-save check used by
 *    the player-edit dialog; returns the conflicts the candidate would
 *    introduce.
 */

// A shirt number is "missing" only when it is genuinely absent (null/empty).
// An explicit 0 / "00" is a real value and is NOT treated as missing.
const isShirtMissing = (v) => v == null || String(v).trim() === '';

export const findSquadDuplicates = (players = [], teamLabel = "") => {
  const byShirt = new Map();
  const missingShirt = [];

  for (const p of players) {
    if (!p) continue;
    if (isShirtMissing(p.shirt_number)) {
      missingShirt.push(p);
    } else {
      const k = Number(p.shirt_number);
      if (!byShirt.has(k)) byShirt.set(k, []);
      byShirt.get(k).push(p);
    }
  }

  const out = [];
  for (const [value, group] of byShirt) {
    if (group.length < 2) continue;
    out.push({
      field: "shirt_number",
      value,
      conflictWith: group[1],
      members: group,
      teamLabel,
    });
  }
  // A missing shirt number is a data-quality issue, not a duplicate. Surface
  // it whenever any player lacks one (even a single one) so the dialog can
  // render it as "data missing" rather than a false shirt-number collision.
  if (missingShirt.length) {
    out.push({
      field: "shirt_number",
      missing: true,
      value: null,
      conflictWith: missingShirt[0],
      members: missingShirt,
      teamLabel,
    });
  }
  return out;
};

export const findPlayerDuplicates = (candidate, teammates = []) => {
  if (!candidate) return [];
  const dupes = [];
  const candidateId = candidate.id || candidate.player_id;

  const candShirt =
    candidate.shirt_number != null && candidate.shirt_number !== ''
      ? Number(candidate.shirt_number)
      : null;
  // Warn the operator when saving a player without a shirt number — surfaced
  // as a "data missing" entry rather than a duplicate.
  if (isShirtMissing(candidate.shirt_number)) {
    dupes.push({ field: 'shirt_number', missing: true, value: null, conflictWith: null });
  }

  for (const t of teammates) {
    if (!t) continue;
    const tId = t.id || t.player_id;
    if (tId && tId === candidateId) continue;

    if (
      candShirt !== null &&
      t.shirt_number != null &&
      t.shirt_number !== '' &&
      Number(t.shirt_number) === candShirt
    ) {
      dupes.push({
        field: 'shirt_number',
        value: candShirt,
        conflictWith: t,
      });
    }
  }

  return dupes;
};
